import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import Swal from "sweetalert2";

import DataTable from "react-data-table-component";
import moment from "moment";
import Toast from "../../utils/Toast";

import { AuthContext } from "../../utils/userProvider";

import useCreditNotes from "../../queries/creditNote/getCreditNotes";
import useCreateAudit from "../../mutation/audit/createAudit";
import Loader from "../../components/Loader";
import { exportCreditNotesToCSV } from "../../utils/csvExport";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const CreditNotes = () => {
  const [creditNoteTable, setCreditNoteTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);
  const [auditResponse, setAuditData] = useCreateAudit();
  const [openDeleteCreditNote, setOpenDeleteCreditNote] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [filterText, setFilterText] = useState("");
  const [selectedYear, setSelectedYear] = useState("next18months");
  const [availableYears] = useState(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Add 3 years above current year
    for (let i = 3; i > 0; i--) {
      years.push(currentYear + i);
    }
    // Add current year
    years.push(currentYear);
    // Add 3 years below current year
    for (let i = 1; i <= 3; i++) {
      years.push(currentYear - i);
    }
    return years;
  });

  const [response, reset] = useCreditNotes(selectedYear);

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  let columns = [
    {
      name: "Guest ID",
      selector: "transaction.guestId",
      sortable: true,
    },
    {
      name: "Reservation ID",
      selector: "transaction.reservationId",
      sortable: true,
    },
    {
      name: "Promo Name/Ref Code",
      selector: (row) =>
        row.promotion
          ? row.promotion.name
          : row.referral_code
          ? "Referral: " + row.referral_code.code
          : "",
      sortable: true,
    },
    {
      name: "Trigger Date",
      selector: "triggerDate",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{moment(row.triggerDate).format("DD MMM YYYY")}</span>;
      },
    },
    {
      name: "Amount",
      selector: "amount",
      sortable: true,
      cell: (row, index, column, id) => {
        return row.amount && <span>A${row.amount.toFixed(2)}</span>;
      },
    },
    {
      name: "Applied",
      selector: "applied",
      sortable: false,
      cell: (row, index, column, id) => {
        return <span>{row.applied ? "Yes" : "No"}</span>;
      },
    },
    {
      name: "Options",
      cell: (row, index, column, id) => {
        if (authState.editAccess) {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CreateIcon />}
                onClick={() => {
                  editItem(row);
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  deleteItem(row);
                }}
              >
                Delete
              </Button>
            </div>
          );
        } else {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  editItem(row);
                }}
              >
                View
              </Button>
            </div>
          );
        }
      },
    },
  ];

  useEffect(() => {
    if (response.data) {
      setCreditNoteTable([...response.data]);
    }
  }, [response.data]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = creditNoteTable.filter(
    (item) =>
      (item.amount &&
        item.amount
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.triggerDate &&
        moment(item.triggerDate)
          .format("DD MMM YYYY")
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.transaction.guestId &&
        item.transaction.guestId
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.transaction.reservationId &&
        item.transaction.reservationId
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.promotion &&
        item.promotion.name
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.referral_code &&
        ("Referral: " + item.referral_code.toString())
          .toLowerCase()
          .includes(filterText.toLowerCase()))
  );

  const editItem = (row) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/creditnote/edit/" + row.id;
    } else {
      window.location.href = "/dashboard/creditnote/view/" + row.id;
    }
  };

  const deleteItem = (row) => {
    setDeleteRow(row);
    setOpenDeleteCreditNote(true);
  };

  const handleClose = () => {
    setOpenDeleteCreditNote(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
            <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "credit-notes",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Credit Note deletion Audit Created",
      });
      setDeleteRow(null);
      setOpenDeleteCreditNote(false);
      reset();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDeleteCreditNote(false);
    }
  }, [auditResponse]);

  const newCreditNote = () => {
    window.location.href = "/dashboard/creditnote/new";
  };

  const handleExportToCSV = () => {
    if (!filteredItems || filteredItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No credit notes available to export.",
      });
      return;
    }

    exportCreditNotesToCSV(
      filteredItems,
      `credit-notes-${new Date().toISOString().split("T")[0]}.csv`
    );
    Swal.fire({
      icon: "success",
      title: "Export Successful",
      text: "Data has been downloaded as CSV file.",
      timer: 2000,
    });
  };

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <div style={{ display: "flex", gap: "10px" }}>
            {authState.role == "Executive" && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={newCreditNote}
              >
                New Credit Note
              </Button>
            )}
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 120 }}
            >
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Year"
              >
                <MenuItem value="last12months">Last 12 Months</MenuItem>
                <MenuItem value="next18months">Next 18 Months</MenuItem>
                <MenuItem value="all">All</MenuItem>
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              id="filter-text"
              onChange={handleFilterText}
              label="Search"
              variant="outlined"
              size="small"
              value={filterText}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleExportToCSV}
            disabled={!filteredItems || filteredItems.length === 0}
            size="small"
          >
            Export to CSV
          </Button>
        </div>
      </div>
    );
  }, [filterText, availableYears, selectedYear, authState.role, filteredItems]);

  return (
    <ListPageShell>
      <ListPageCard>
        {response.loading && <Loader />}
        {!response.loading && (
          <div>
            <ListPageHeader title="Credit Notes" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30]}
              noHeader={true}
              subHeader
              data={filteredItems}
              columns={columns}
              subHeaderComponent={subHeaderComponentMemo}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        open={openDeleteCreditNote}
        onClose={(event, reason) => {
          if (
            auditResponse.loading &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus
        sx={modalSx}
      >
        <Fade in={openDeleteCreditNote}>
          <Box>
            <DeleteModalFrame title="Delete Credit Note">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete this credit note?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="secondary"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default CreditNotes;
